/**
 * <recently-viewed-products> custom element.
 * Tracks recently viewed products in localStorage and renders them as a card grid.
 *
 * Authoring lives in sections/recently-viewed.liquid. Markup expected:
 *   <recently-viewed-products
 *     data-storage-key="wsRecentlyViewed"
 *     data-max-items="6"
 *     data-current-id="123"           (only on PDP)
 *     data-current-handle="my-shoe"   (only on PDP)
 *     data-current-title="…"          (only on PDP)
 *     data-current-image="https://…"  (only on PDP)
 *     data-current-price="49.99"      (only on PDP)
 *     data-current-url="/products/my-shoe" (only on PDP)
 *     data-currency="EUR"
 *     data-quickview-text="Quick view">
 *     <div class="recently-viewed__grid"></div>
 *   </recently-viewed-products>
 */
class RecentlyViewedProducts extends HTMLElement {
  connectedCallback() {
    this.storageKey = this.getAttribute('data-storage-key') || 'wsRecentlyViewed';
    this.maxItems = parseInt(this.getAttribute('data-max-items'), 10) || 6;
    this.currency = this.getAttribute('data-currency') || 'EUR';
    this.quickviewText = this.getAttribute('data-quickview-text') || '';

    this.trackCurrent();
    this.render();
  }

  readList() {
    try {
      var raw = localStorage.getItem(this.storageKey);
      var list = raw ? JSON.parse(raw) : [];
      return Array.isArray(list) ? list : [];
    } catch (e) { return []; }
  }

  writeList(list) {
    try { localStorage.setItem(this.storageKey, JSON.stringify(list)); } catch (e) { }
  }

  trackCurrent() {
    var id = this.getAttribute('data-current-id');
    if (!id) return;
    var item = {
      id: id,
      handle: this.getAttribute('data-current-handle') || '',
      title: this.getAttribute('data-current-title') || '',
      image: this.getAttribute('data-current-image') || '',
      price: this.getAttribute('data-current-price') || '',
      compare: this.getAttribute('data-current-compare') || '',
      url: this.getAttribute('data-current-url') || '',
      ts: Date.now()
    };
    var list = this.readList().filter(function (i) { return String(i.id) !== String(id); });
    list.unshift(item);
    list = list.slice(0, 12);
    this.writeList(list);
  }

  formatMoney(value) {
    var num = parseFloat(value);
    if (!isFinite(num)) return '';
    // Der Shop formatiert Preise überall über theme.moneyFormat ("€16,99").
    // toLocaleString liefert "16,99 €" und würde in den Karten anders aussehen
    // als auf jeder anderen Seite. Der gespeicherte Wert steht in Euro, die
    // globale Funktion erwartet Cent.
    if (typeof window.formatMoney === 'function' && window.theme && window.theme.moneyFormat) {
      return window.formatMoney(Math.round(num * 100));
    }
    try {
      return num.toLocaleString(undefined, { style: 'currency', currency: this.currency });
    } catch (e) {
      return num.toFixed(2);
    }
  }

  escAttr(s) { return (s || '').replace(/"/g, '&quot;'); }
  escHtml(s) {
    return (s || '').replace(/[&<>"']/g, function (c) {
      return ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c];
    });
  }

  cardHTML(p) {
    var title = this.escHtml(p.title);
    var titleAttr = this.escAttr(p.title);
    var url = p.url || (p.handle ? '/products/' + p.handle : '#');
    var img = p.image || '//cdn.shopify.com/s/images/admin/no-image-large.gif';
    var priceHtml = '';
    if (p.price) {
      var nowPrice = this.formatMoney(p.price);
      if (p.compare && parseFloat(p.compare) > parseFloat(p.price)) {
        priceHtml =
          '<s class="recently-viewed__compare">' + this.formatMoney(p.compare) + '</s>' +
          '<span class="recently-viewed__price">' + nowPrice + '</span>';
      } else {
        priceHtml = '<span class="recently-viewed__price">' + nowPrice + '</span>';
      }
    }
    return (
      '<div class="recently-viewed__card">' +
        '<a class="recently-viewed__media" href="' + url + '" aria-label="' + titleAttr + '">' +
          '<img loading="lazy" src="' + img + '" alt="' + titleAttr + '">' +
        '</a>' +
        '<div class="recently-viewed__body">' +
          '<a class="recently-viewed__title" href="' + url + '">' + title + '</a>' +
          (priceHtml ? '<div class="recently-viewed__prices">' + priceHtml + '</div>' : '') +
        '</div>' +
      '</div>'
    );
  }

  render() {
    var grid = this.querySelector('.recently-viewed__grid');
    if (!grid) return;
    var currentId = this.getAttribute('data-current-id');
    var list = this.readList();
    if (currentId) list = list.filter(function (i) { return String(i.id) !== String(currentId); });
    list = list.slice(0, this.maxItems);
    if (!list.length) {
      this.style.display = 'none';
      return;
    }
    var self = this;
    grid.innerHTML = list.map(function (p) { return self.cardHTML(p); }).join('');
    this.style.display = '';
  }
}

if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}
